import { Routes } from "@blitzjs/next"
import { TAnalyticsEvent } from "@superwall/legacy-api-utils"
import useAnalytics from "app/analytics/hooks/useAnalytics"
import { getLoginRoute, useSession } from "app/authv2/use-session"
import useUtmParams from "app/core/hooks/useUTMParams"
import Image from "next/legacy/image"
import Link from "next/link"
import { useRouter } from "next/router"
import LogoIcon from "public/Icon_Dark_Mode.svg"
import LogoImage from "public/SuperwallLogo.svg"
import { useEffect, useState } from "react"

const Logo = () => {
  const { passUtmParams } = useUtmParams()
  return (
    <Link href={passUtmParams("/")} passHref={true} className="scale-1">
      {/* mobile logo */}
      <span className="items-center justify-center flex md:hidden scale-[0.7] origin-left -ml-0">
        <Image alt={"logo"} src={LogoImage} />
      </span>
      {/* tablet logo */}
      <span className="items-center justify-center hidden md:flex lg:hidden translate-y-[3px]">
        <Image width={55} height={55} alt={"logo"} src={LogoIcon} />
      </span>
      {/* desktop logo */}
      <span className="items-center justify-center hidden lg:flex">
        <Image alt={"logo"} src={LogoImage} />
      </span>
    </Link>
  )
}

const Menu = ({ showBookDemo = true }: { showBookDemo?: boolean }) => {
  const { passUtmParams } = useUtmParams()
  const { isLoading, isLoggedIn } = useSession()

  const track = useAnalytics()
  const router = useRouter()

  return (
    <div
      className={`flex flex-row items-center gap-6 lg:gap-7 backdrop-blur-xl bg-grayscale-lightest border border-1 border-white border-opacity-10 bg-opacity-10 shadow-xl rounded-full pl-6 ${
        showBookDemo ? "!pr-1" : "pr-6 lg:pr-7 !h-[45px]"
      } h-[44px] lg:h-auto lg:px-7 lg:py-1`}
    >
      <Link
        passHref={true}
        href={passUtmParams("https://superwall.com/docs/home")}
        style={{ textShadow: "0px 1px 2px rgba(0,0,0,0.4)" }}
        className="text-white hover:text-brand-primary transition-all text-base xl:text-[17px]"
        target="_blank"
      >
        Docs
      </Link>
      <a
        style={{ textShadow: "0px 1px 2px rgba(0,0,0,0.4)" }}
        className="text-white hover:text-brand-primary transition-all cursor-pointer text-base xl:text-[17px]"
        onClick={() => {
          if (window && window !== undefined) {
            const elementId = "pricing-anchor"
            const element = document.getElementById(elementId)
            console.log("CLICK element", element)

            window?.scrollTo({
              top: (element?.getBoundingClientRect().top || 0) + window.pageYOffset,
              behavior: "smooth",
            })

            if (!element) {
              router.push("/#pricing-anchor")
            }
          }
        }}
      >
        Pricing
      </a>
      <Link
        passHref={true}
        href={passUtmParams(`/blog`)}
        style={{ textShadow: "0px 1px 2px rgba(0,0,0,0.4)" }}
        className="text-white hover:text-brand-primary transition-all text-base xl:text-[17px]"
      >
        Blog
      </Link>
      <Link
        passHref={true}
        href={passUtmParams(`https://www.paywallscreens.com/`)}
        style={{ textShadow: "0px 1px 2px rgba(0,0,0,0.4)" }}
        className="text-white hover:text-brand-primary transition-all text-base xl:text-[17px] hidden lg:block"
        target="_blank"
      >
        Inspiration
      </Link>
      <Link
        passHref={true}
        href={passUtmParams(`/templates`)}
        style={{ textShadow: "0px 1px 2px rgba(0,0,0,0.4)" }}
        className="text-white hover:text-brand-primary transition-all text-base xl:text-[17px] hidden xl:block"
      >
        Templates
      </Link>
      {showBookDemo && (
        <Link
          passHref={true}
          href={passUtmParams(`/contact/book-a-demo`)}
          className={` ${
            isLoggedIn || isLoading
              ? "text-white bg-white/7 border border-1 border-white border-opacity-7"
              : "text-black bg-brand-primary font-medium"
          }  hover:text-opacity-50 transition-all cursor-pointer text-base xl:text-[17px] h-[35px] lg-h-[40px] flex items-center justify-center px-3 lg:px-5 rounded-full`}
          onClick={() => {
            track({
              name: "DemoRequest_Begin",
              properties: {
                source: "header",
              },
              group: {
                kind: "none",
                object: {},
              },
            } as TAnalyticsEvent)
          }}
        >
          {isLoggedIn || isLoading ? "Demo" : "Get a Demo"}
        </Link>
      )}
    </div>
  )
}

const Account = () => {
  const { isLoading, isLoggedIn } = useSession()

  const { utmParams } = useUtmParams()

  if (isLoading) {
    return <></>
  }

  return (
    <div className="flex flex-row gap-2 sm:gap-4 items-center justify-end">
      {!isLoggedIn ? (
        <>
          <Link href={getLoginRoute()} passHref legacyBehavior prefetch={false}>
            <span className="hidden sm:flex mr-1 xl:text-[17px] lg:text-xl font-medium items-center justify-center text-brand-primary cursor-pointer">
              Log in
            </span>
          </Link>
          <Link href={"/api/auth/signup"} passHref legacyBehavior prefetch={false}>
            <span className="text-sm sm:text-base xl:text-[17px] font-medium flex items-center justify-center bg-brand-primary rounded-full text-[#191F27] px-3 sm:px-4 md:px-5 h-[34px] sm:h-[38px] md:h-auto md:py-2 cursor-pointer whitespace-nowrap">
              Sign Up <span className="ml-1 md:ml-2">→</span>
            </span>
          </Link>
        </>
      ) : (
        <>
          <Link href={Routes.Dashboard()} passHref legacyBehavior>
            <span className="text-sm sm:text-base xl:text-[17px] font-medium flex items-center justify-center bg-brand-primary rounded-full text-[#191F27] px-3 sm:px-4 md:px-5 h-[34px] sm:h-[38px] md:h-auto md:py-2 cursor-pointer whitespace-nowrap">
              Dashboard <span className="ml-1 md:ml-2">→</span>
            </span>
          </Link>
        </>
      )}
    </div>
  )
}

const BurgerIcon = ({ isOpen, onClick }: { isOpen: boolean; onClick: () => void }) => {
  return (
    <button
      onClick={onClick}
      className={`relative h-[34px] w-[34px] sm:h-[38px] sm:w-[38px] focus:outline-none rounded-full ${
        isOpen ? "bg-white/10" : "hover:bg-white/10"
      } transition-colors duration-200 flex items-center justify-center`}
      aria-label={isOpen ? "Close menu" : "Open menu"}
    >
      <div className="w-4 h-4 sm:w-5 sm:h-5 relative flex items-center justify-center">
        <span
          className={`absolute h-[1.5px] sm:h-[2px] w-4 sm:w-5 bg-white transition-all duration-300 ${
            isOpen ? "rotate-45" : "-translate-y-[3px] sm:-translate-y-[4px]"
          }`}
        ></span>
        <span
          className={`absolute h-[1.5px] sm:h-[2px] w-4 sm:w-5 bg-white transition-all duration-300 ${
            isOpen ? "-rotate-45" : "translate-y-[3px] sm:translate-y-[4px]"
          }`}
        ></span>
      </div>
    </button>
  )
}

const MobileMenu = ({
  showBookDemo = true,
  isOpen,
}: {
  showBookDemo?: boolean
  isOpen: boolean
}) => {
  const { passUtmParams } = useUtmParams()
  const { isLoading, isLoggedIn } = useSession()
  const track = useAnalytics()
  const router = useRouter()

  return (
    <div
      className={`fixed inset-x-0 top-0 bottom-0 z-[90] transition-all md:hidden duration-200 ease-in-out ${
        isOpen ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      <div className="absolute inset-0 backdrop-blur-xl bg-grayscale-dark bg-opacity-90 pointer-events-none"></div>
      <div className="relative h-full pt-[60px] md:pt-[85px]">
        <div className="flex flex-col items-stretch gap-2.5 pt-8 overflow-y-auto h-full px-4">
          <Link
            passHref={true}
            href={passUtmParams("https://superwall.com/docs/home")}
            className="text-white text-2xl text-center py-2"
          >
            Docs
          </Link>
          <a
            className="text-white text-2xl text-center py-2 cursor-pointer"
            onClick={() => {
              if (window && window !== undefined) {
                const elementId = "pricing-anchor"
                const element = document.getElementById(elementId)
                console.log("CLICK element", element)

                window?.scrollTo({
                  top: (element?.getBoundingClientRect().top || 0) + window.pageYOffset,
                  behavior: "smooth",
                })

                if (!element) {
                  router.push("/#pricing-anchor")
                }
              }
            }}
          >
            Pricing
          </a>
          <Link
            passHref={true}
            href={passUtmParams(`/blog`)}
            className="text-white text-2xl text-center py-2"
          >
            Blog
          </Link>
          <Link
            passHref={true}
            href={passUtmParams(`https://www.paywallscreens.com/`)}
            className="text-white text-2xl text-center py-2"
            target="_blank"
          >
            Inspiration
          </Link>
          <Link
            passHref={true}
            href={passUtmParams(`/templates`)}
            className="text-white text-2xl text-center py-2"
          >
            Templates
          </Link>
          {!isLoggedIn && (
            <Link
              href={"/api/auth/login"}
              passHref
              className="text-brand-primary text-2xl text-center py-2 sm:hidden"
            >
              Log in
            </Link>
          )}
          {showBookDemo && (
            <div className="flex justify-center mt-2">
              <Link
                passHref={true}
                href={passUtmParams(`/contact/book-a-demo`)}
                className="text-black bg-brand-primary font-medium text-2xl px-6 py-2 rounded-full"
                onClick={() => {
                  track({
                    name: "DemoRequest_Begin",
                    properties: {
                      source: "header",
                    },
                    group: {
                      kind: "none",
                      object: {},
                    },
                  } as TAnalyticsEvent)
                }}
              >
                {isLoggedIn || isLoading ? "Demo" : "Get a Demo"}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const Header = ({ showBookDemo = true }: { showBookDemo?: boolean }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // This is to prevent scrolling when the mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }

    return () => {
      document.body.style.overflow = "unset"
    }
  }, [mobileMenuOpen])

  return (
    <>
      <header className="fixed top-0 right-0 left-0 z-[100]">
        <div className="w-full h-[60px] md:h-[85px] backdrop-blur-xl bg-grayscale-darkest block lg:hidden bg-opacity-20 border-b border-b-1 border-white border-opacity-10 absolute pointer-events-none"></div>
        <div className="w-full relative flex items-center bg-gradient-to-b from-[#00000050] mx-auto h-[60px] md:h-[85px] px-4 sm:px-5 lg:px-8">
          <div>
            <Logo />
          </div>
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2">
            <Menu showBookDemo={showBookDemo} />
          </div>
          <div className="ml-auto flex items-center gap-2 sm:gap-4">
            <Account />
            <div className="text-white sm:-ml-2 md:hidden">
              <BurgerIcon
                isOpen={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              />
            </div>
          </div>
        </div>
      </header>
      <MobileMenu showBookDemo={showBookDemo} isOpen={mobileMenuOpen} />
    </>
  )
}

export default Header
